import React from "react";

const SITE_NAME = "Affiliate Hub";
const SITE_URL = process.env.REACT_APP_SITE_URL || "https://affiliatehub.com";

// Default SEO values
const defaults = {
  title: "Affiliate Hub - Honest Software Reviews & Comparisons",
  description: "Find the best software tools with unbiased reviews, detailed comparisons, and data-driven recommendations. Make informed decisions with our trusted affiliate reviews.",
  image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200",
  type: "website",
};

/**
 * SEO Component for managing page meta tags using React 19 native metadata support
 * 
 * @param {Object} props
 * @param {string} props.title - Page title (will be appended with site name)
 * @param {string} props.description - Meta description
 * @param {string} props.image - OG image URL
 * @param {string} props.url - Canonical URL path (e.g., "/ai-video-tools")
 * @param {string} props.type - OG type (website, article, product)
 * @param {Object} props.article - Article-specific data {published_time, modified_time, author}
 * @param {Object} props.product - Product-specific data {name, price, rating}
 * @param {string} props.keywords - Meta keywords
 * @param {boolean} props.noindex - Set to true to prevent indexing
 */
const SEO = ({
  title,
  description,
  image,
  url,
  type = "website",
  article,
  product,
  keywords,
  noindex = false,
}) => {
  const pageTitle = title ? `${title} | ${SITE_NAME}` : defaults.title;
  const pageDescription = description || defaults.description;
  const pageImage = image || defaults.image;
  const pageUrl = url ? `${SITE_URL}${url}` : SITE_URL;
  const pageType = type || defaults.type;

  // Debug log to check if component is rendering
  console.log("SEO Component rendering:", { pageTitle, pageDescription });

  return (
    <>
      {/* Basic Meta Tags - Using React 19 native metadata support */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={pageUrl} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large" />
      )}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:type" content={pageType} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />

      {/* Article-specific tags */}
      {article && (
        <>
          {article.published_time && (
            <meta property="article:published_time" content={article.published_time} />
          )}
          {article.modified_time && (
            <meta property="article:modified_time" content={article.modified_time} />
          )}
          {article.author && (
            <meta property="article:author" content={article.author} />
          )}
        </>
      )}

      {/* Product-specific structured data */}
      {product && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": product.name,
            "description": pageDescription,
            "image": pageImage,
            "url": pageUrl,
            ...(product.rating && {
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": product.rating,
                "bestRating": "5",
                "worstRating": "1",
                "ratingCount": product.ratingCount || 100
              }
            }),
            ...(product.price && {
              "offers": {
                "@type": "Offer",
                "price": product.price.replace(/[^0-9.]/g, ''),
                "priceCurrency": "USD",
                "availability": "https://schema.org/InStock"
              }
            })
          })}
        </script>
      )}

      {/* Organization structured data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": SITE_NAME,
          "url": SITE_URL,
          "logo": `${SITE_URL}/logo.png`,
          "description": defaults.description
        })}
      </script>
    </>
  );
};

// Preset configurations for common page types
export const SEOPresets = {
  home: {
    title: null, // Uses default
    description: defaults.description,
    type: "website",
  },
  niche: (niche) => ({
    title: `Best ${niche.name} ${new Date().getFullYear()} - Reviews & Comparison`,
    description: niche.meta_description || niche.description,
    url: `/${niche.slug}`,
    type: "website",
    keywords: `${niche.name}, best ${niche.name.toLowerCase()}, ${niche.name.toLowerCase()} comparison, ${niche.name.toLowerCase()} reviews`,
  }),
  product: (product, niche) => ({
    title: `${product.name} Review ${new Date().getFullYear()} - Pros, Cons & Pricing`,
    description: `${product.name} review: ${product.tagline}. Rating: ${product.rating}/5. Starting at ${product.starting_price || 'Contact for pricing'}.`,
    url: `/products/${product.slug}`,
    type: "article",
    image: product.logo_url,
    keywords: `${product.name} review, ${product.name} pricing, ${product.name} pros cons, ${niche?.name || 'software'} comparison`,
    product: {
      name: product.name,
      price: product.starting_price,
      rating: product.rating,
      ratingCount: product.rating_count,
    },
    article: {
      published_time: product.created_at,
      modified_time: product.updated_at,
      author: SITE_NAME,
    },
  }),
  comparison: (products, niche) => ({
    title: `${niche.name} Comparison ${new Date().getFullYear()} - ${products.slice(0, 3).map(p => p.name).join(' vs ')}`,
    description: `Compare the best ${niche.name.toLowerCase()}: ${products.slice(0, 5).map(p => p.name).join(', ')}. Side-by-side features, pricing, and honest reviews.`,
    url: `/${niche.slug}`,
    type: "website",
    keywords: products.map(p => p.name).join(', ') + `, ${niche.name.toLowerCase()} comparison`,
  }),
};

export default SEO;